import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./verification.css";
import verification_mark from "../../assets/verification_mark.png";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function Verification() {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(20);
  const [tick, setTick] = useState(false);
  const [resending, setResending] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  const email = localStorage.getItem("pendingEmail");

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
    
    localStorage.setItem("verification_start_time", new Date().toISOString());
    localStorage.setItem("verification_timezone", timeZone);
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((t) => t - 1);
        setTick(true);
        setTimeout(() => setTick(false), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < code.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
    if (e.key === "Enter" && code.join("").trim()) {
      handleConfirm();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").trim();
    if (/^\d+$/.test(paste)) {
      const digits = paste.split("").slice(0, code.length);
      const newCode = [...code];
      digits.forEach((digit, i) => (newCode[i] = digit));
      setCode(newCode);
      
      const lastFilledIndex = digits.length - 1;
      if (lastFilledIndex < code.length - 1) {
        setTimeout(() => {
          inputsRef.current[lastFilledIndex + 1]?.focus();
        }, 10);
      }
    }
    e.preventDefault();
  };

  const handleConfirm = async () => {
    if (!code.join("").trim()) return toast.error("Enter verification code");
    try {
      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/verify-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          code: code.join(""),
          purpose: "email_verification",
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.detail || "Invalid code. Try again.");
        
        localStorage.setItem("verification_failed_time", new Date().toISOString());
        return;
      }

      if (data?.access && data?.refresh) {
        localStorage.setItem("accessToken", data.access);
        localStorage.setItem("refreshToken", data.refresh);
      }

      localStorage.setItem("verification_success_time", new Date().toISOString());
      localStorage.setItem("verification_attempts", "success");
      
      if (window.gtag) {
        window.gtag("event", "email_verified", {
          email_domain: email?.split('@')[1],
          verification_method: "email_code",
          timezone: userTimeZone,
        });
      }

      toast.success("Verification successful!");

      const userStatus = localStorage.getItem("user_status");
      if (userStatus === "worker") {
        localStorage.setItem("redirectAfterPassword", "/worker-invnit");
      } else {
        localStorage.setItem("redirectAfterPassword", "/account-type-select");
      }

      navigate("/create-password");
    } catch (err) {
      console.error("❌ VERIFY ERROR:", err);
      toast.error("Verification failed. Try again later.");
      
      if (window.gtag) {
        window.gtag("event", "verification_error", {
          error_type: "network",
        });
      }
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    
    try {
      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/resend-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          email, 
          purpose: "email_verification",
          timeZone: userTimeZone,
        }),
      });
      
      if (!res.ok) throw new Error();
      
      setTimeLeft(20);
      setCode(["", "", "", "", "", ""]);
      
      localStorage.setItem("verification_resend_time", new Date().toISOString());
      localStorage.setItem("verification_resend_count", 
        (parseInt(localStorage.getItem("verification_resend_count") || 0) + 1).toString()
      );
      
      toast.success("Code resent successfully!");
      
      if (window.gtag) {
        window.gtag("event", "code_resent", {
          email_domain: email?.split('@')[1],
        });
      }
      
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      
    } catch {
      toast.error("Failed to resend code.");
    } finally {
      setTimeout(() => setResending(false), 1000);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && code.join("").trim()) {
        e.preventDefault();
        handleConfirm();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [code]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="verify">
      <main className="verify-main">
        <div className="verify-card">
          <div className="verify-avatar">
            <img src={verification_mark} alt="icon" />
          </div>
          <h2 className="verify-title">Verification Code</h2>
          <p className="verify-subtitle">
            We have sent a verification code to <b>{email}</b>
          </p>

          <div className="verify-code-inputs">
            {code.map((digit, i) => (
              <input
                key={i}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                ref={(el) => (inputsRef.current[i] = el)}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
              />
            ))}
          </div>

          {timeLeft > 0 ? (
            <p className="verify-resend-wrapper">
              <span className="verify-resend-label">Send code again</span>
              <span className={`verify-timer ${tick ? "tick" : ""}`}>
                00:{timeLeft.toString().padStart(2, "0")}
              </span>
            </p>
          ) : (
            <>
              {!resending ? (
                <button className="verify-resend-btn fade-in" onClick={handleResend}>
                  Resend code
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="verify-loader"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.2,
                      ease: "linear",
                    }}
                    className="verify-spinner"
                  />
                  <p>Sending code...</p>
                </motion.div>
              )}
            </>
          )}

          <button 
            className="verify-confirm-btn" 
            onClick={handleConfirm}
            disabled={!code.join("").trim()}
          >
            Confirm
          </button>
        </div>
      </main>

      <footer className="verify-footer">
        <span>© 2025 Halper. All rights reserved.</span>
        <div>
          <a href="#">
            <IoMdLock /> Privacy
          </a>
          <a href="#">
            <CiCircleQuestion /> Get help
          </a>
        </div>
      </footer>
    </div>
  );
}